import * as React from 'react'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'

import {
  Input,
  InputProps
} from '@espoon-voltti/react-components/elements/form'

import { EuroCents } from '@espoon-voltti/ts-libs/lib/euro/domain'
import { format } from '@espoon-voltti/ts-libs/lib/euro/format'
import { parse } from '@espoon-voltti/ts-libs/lib/euro/parse'

interface EuroInputProps extends Omit<InputProps, 'onChange' | 'value'> {
  onChange: (euros: EuroCents) => void
  value: EuroCents | null
  max?: number
  min?: number // rename to negative max (as this is only relevant when negative)?
}

const formatDisplayValue = (x: EuroCents | null) =>
  pipe(
    x,
    O.fromNullable,
    O.map(format),
    O.getOrElse(() => '')
  )

const MAX_DEFAULT = 21474836.47 // cents fit into 32 bit signed integer
const MIN_DEFAULT = 0
export const EuroInput: React.FC<EuroInputProps> = ({
  max = MAX_DEFAULT,
  min = MIN_DEFAULT,
  ...p
}) => {
  const [state, setState] = React.useState<string>(formatDisplayValue(p.value))
  const [focused, setFocused] = React.useState<boolean>(false)

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select()
    setFocused(true)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setState(formatDisplayValue(p.value))
    setFocused(false)
    if (p.onBlur) {
      p.onBlur(e)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const candidate = e.target.value.trim()

    if (candidate === '' || (candidate === '-' && min < 0)) {
      setState(candidate)
      p.onChange(EuroCents.zero())
      return
    }

    const maybeCents = parse(candidate)
    if (O.isSome(maybeCents)) {
      const cents = maybeCents.value
      const euros = cents.euros()

      if (min < 0) {
        if (min <= euros && euros <= max) {
          setState(candidate)
          p.onChange(cents)
        }
      } else {
        if (euros <= max) {
          setState(candidate)
          p.onChange(cents)
        }
      }
    }

    // any other inputs are rejected
  }

  React.useEffect(() => {
    if (!focused) {
      setState(formatDisplayValue(p.value))
    }
  }, [p.value, focused])

  const { value, onChange, ...inputProps } = p
  return (
    <Input
      {...inputProps}
      type="text"
      value={state}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
    />
  )
}
